import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import ResponsiveView from "../../components/responsive/ResponsiveView";
import DataCard from "../../components/responsive/DataCard";
import DualCurrencyValue from "../../components/money/DualCurrencyValue";
import { fetchAdminRegisterSummary } from "../../services/cashService";
import styles from "../shifts/shifts.module.scss";

const RANGE_OPTIONS = [
    { label: "All Time", value: "all" },
    { label: "Today", value: "day" },
    { label: "This Week", value: "week" },
    { label: "This Month", value: "month" },
];

const staffName = (staff) => (staff ? `${staff.firstName || ""} ${staff.lastName || ""}`.trim() || staff.email : "Unknown");
const pairHasDiscrepancy = (p) => !!(p && ((p.usd && Math.abs(p.usd) >= 0.01) || (p.jmd && Math.abs(p.jmd) >= 0.01)));

const AdminCashRegisterDetail = () => {
    const { id } = useParams();
    const history = useHistory();
    const [range, setRange] = useState("all");
    const [dateRange, setDateRange] = useState(null);
    const [loading, setLoading] = useState(true);
    const [register, setRegister] = useState(null);
    const [totals, setTotals] = useState(null);
    const [currentBalance, setCurrentBalance] = useState({ usd: 0, jmd: 0 });
    const [currentHolder, setCurrentHolder] = useState(null);
    const [logs, setLogs] = useState([]);

    const load = async () => {
        setLoading(true);
        let res;
        if (dateRange && dateRange.length === 2 && dateRange[1]) {
            res = await fetchAdminRegisterSummary(id, null, dateRange[0].toISOString(), dateRange[1].toISOString());
        } else {
            res = await fetchAdminRegisterSummary(id, range);
        }
        setRegister(res?.data?.register || null);
        setTotals(res?.data?.totals || null);
        setCurrentBalance(res?.data?.currentBalance || { usd: 0, jmd: 0 });
        setCurrentHolder(res?.data?.currentHolder || null);
        setLogs(res?.data?.logs || []);
        setLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, range, dateRange]);

    const logAcquiredBody = (row) => (row.acquiredAt ? new Date(row.acquiredAt).toLocaleString() : "-");
    const logReleasedBody = (row) => (row.releasedAt ? new Date(row.releasedAt).toLocaleString() : "-");
    const logStaffBody = (row) => staffName(row.staff);
    const logStatusBody = (row) => <span className={`${styles.badge} ${row.status === "open" ? styles.badgeActive : styles.badgePending}`}>{row.status === "open" ? "Currently Holding" : "Closed"}</span>;
    const logStartingCashBody = (row) => (row.startingCash === null || row.startingCash === undefined ? "-" : <DualCurrencyValue pair={row.startingCash} compact />);
    const logClosingCashBody = (row) => (row.closingCash === null || row.closingCash === undefined ? "-" : <DualCurrencyValue pair={row.closingCash} compact />);
    const logDiscrepancyBody = (row) => {
        if (row.discrepancy === null || row.discrepancy === undefined) return "-";
        return <DualCurrencyValue pair={row.discrepancy} compact danger={pairHasDiscrepancy(row.discrepancy)} />;
    };

    return (
        <div className={styles.page}>
            <div className={styles.backLink} onClick={() => history.push("/admin/cash-registers")}>
                <i className="pi pi-arrow-left" /> Back to Registers
            </div>

            <div className={styles.pageHeader}>
                <div className={styles.headerTitleRow}>
                    <div className={styles.headerIcon}>
                        <i className="pi pi-inbox" />
                    </div>
                    <div>
                        <h2>{register?.name || "Register"}</h2>
                        <p className={styles.subtitle}>Cash activity for this register over a selected date range</p>
                    </div>
                </div>
            </div>

            <div className={styles.filtersSection}>
                <div className={styles.filterGroup}>
                    <label>Quick Range</label>
                    <div className={styles.quickRangeRow}>
                        {RANGE_OPTIONS.map((opt) => (
                            <Button
                                key={opt.value}
                                label={opt.label}
                                className={`${styles.quickRangeBtn} ${range === opt.value && !dateRange ? styles.activeRange : ""}`}
                                onClick={() => {
                                    setDateRange(null);
                                    setRange(opt.value);
                                }}
                            />
                        ))}
                    </div>
                </div>
                <div className={styles.filterGroup}>
                    <label htmlFor="dateRange">Custom Range</label>
                    <Calendar id="dateRange" value={dateRange} onChange={(e) => setDateRange(e.value)} selectionMode="range" readOnlyInput placeholder="Select date range" />
                </div>
            </div>

            {loading ? (
                <div className={styles.loadingState}>
                    <ProgressSpinner strokeWidth="4" />
                    <span>Loading register activity...</span>
                </div>
            ) : (
                <>
                    <div className={styles.statsRow}>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-arrow-down" />
                            </div>
                            <div>
                                <DualCurrencyValue pair={totals?.cashIn} />
                                <div className={styles.statLabel}>Cash In</div>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-arrow-up" />
                            </div>
                            <div>
                                <DualCurrencyValue pair={totals?.cashOut} />
                                <div className={styles.statLabel}>Cash Out</div>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-wallet" />
                            </div>
                            <div>
                                <DualCurrencyValue pair={totals?.net} />
                                <div className={styles.statLabel}>Net Movement (Period)</div>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-lock" />
                            </div>
                            <div>
                                <DualCurrencyValue pair={currentBalance} />
                                <div className={styles.statLabel}>Current Balance{currentHolder ? ` (held by ${currentHolder})` : " (last counted)"}</div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionCardHeader}>
                        <h3>
                            <i className="pi pi-history" />
                            Register Logs
                        </h3>
                    </div>
                    <ResponsiveView
                        value={logs}
                        emptyMessage="No register activity for this period."
                        storageKey="adminCashRegisterDetail.logs"
                        renderCard={(row, i) => (
                            <DataCard
                                key={`${row.shift}-${row.acquiredAt || i}`}
                                accent={row.status === "open" ? "green" : pairHasDiscrepancy(row.discrepancy) ? "amber" : undefined}
                                title={logStaffBody(row)}
                                subtitle={logAcquiredBody(row)}
                                badge={logStatusBody(row)}
                                fields={[
                                    { label: "Starting Cash", value: logStartingCashBody(row) },
                                    { label: "Closing Cash", value: logClosingCashBody(row) },
                                    { label: "Released", value: logReleasedBody(row) },
                                    { label: "Discrepancy", value: logDiscrepancyBody(row) },
                                ]}
                            />
                        )}
                    >
                        <DataTable value={logs} paginator rows={20} rowsPerPageOptions={[20, 50, 100]} className="datatable-responsive custom-datatable" emptyMessage="No register activity for this period." rowHover>
                            <Column header="Acquired" body={logAcquiredBody} />
                            <Column header="Staff" body={logStaffBody} />
                            <Column header="Status" body={logStatusBody} />
                            <Column header="Starting Cash" body={logStartingCashBody} />
                            <Column header="Closing Cash" body={logClosingCashBody} />
                            <Column header="Released" body={logReleasedBody} />
                            <Column header="Discrepancy" body={logDiscrepancyBody} />
                        </DataTable>
                    </ResponsiveView>
                </>
            )}
        </div>
    );
};

export default AdminCashRegisterDetail;
